// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/onboardingContent.js
//  MICROSERVICIO ONBOARDING v3 — Copy aprobado del "Motor de Diagnóstico"
//
//  ⚠️  REGLA INQUEBRANTABLE (Parte III del documento maestro):
//      Todo el texto de este archivo está TRANSCRITO LITERALMENTE del documento
//      maestro. NO se reescribe, NO se resume, NO se "mejora". Si el copy debe
//      cambiar, se cambia primero en el documento maestro y luego aquí.
//
//  ── DIRECTIVA GLOBAL ESTRATÉGICA (v2.1 / v2.2) ──────────────────────────────
//      "El idioma no se estudia, se entrena."
//      NSL es un GIMNASIO DE INGLÉS, no un curso milagroso. Spacy es un personal
//      trainer que diseña rutinas. Nada de este copy promete fluidez en X meses.
//      Vocabulario activo, no pasivo: entrenar (no aprender), rutina/plan de
//      entrenamiento (no curso), usuario en forma (no estudiante).
//
//  Fuente: docs/correccionesOnboarding.md (Documento Maestro v2.2)
//  Mapa de origen:
//    ASPIRATIONS        → §1 Pantalla 1 — El Gran Objetivo          [v2.1]
//    TRIGGERS           → §2 Pantalla 2 P1 — El Detonante           [v2.1]
//    TRAINING_INTENSITY → §2 Pantalla 2 P2 — Nivel de Intensidad    [v2.1]
//    CONTEXTS           → §3 Pantalla 3
//    PERCEIVED/ATTEMPTS → §4 Pantalla 4
//    CALIBRATION_*      → §0, §5.1, §5.2
//    CONTACT_CAPTURE    → §5.bis Pantalla 5.5
//    FEEDBACK_STYLES    → §6 Pantalla 6 — Estilo de Entrenamiento   [v2.2]
//    PROCESSING/REVEAL  → §7.1, §7.2
//    RETURN_*           → §7.3
// ═══════════════════════════════════════════════════════════════════════════════

/** Valor sentinela de las opciones de texto libre ("Otro objetivo / motivo"). */
export const CUSTOM_OPTION = 'other_custom';

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 1 — El Gran Objetivo (§1) [ACTUALIZADO v2.1]
   ───────────────────────────────────────────────────────────────────────────── */

export const SCREEN1_HEADLINE = {
  linea: 'Imagina que hoy dejas de traducir en tu cabeza y el inglés fluye natural. ',
  bloque: '¿Cuál es el gran objetivo que te gustaría lograr?',
};

export const ASPIRATIONS = [
  {
    value: 'lead_meeting_without_hesitation',
    icon: '💼',
    label: 'Liderar reuniones y proyectos profesionales sin dudar.',
    reaction: 'Anotado. Vamos a enfocar tu vocabulario en ambientes profesionales y de negocios desde el día uno.',
    // `short` alimenta el copy con variables de la Pantalla 7 ("Tu ruta hacia [aspiration]")
    short: 'liderar reuniones y proyectos sin dudar',
  },
  {
    value: 'travel_without_depending_on_anyone',
    icon: '✈️',
    label: 'Viajar, conectar y explorar el mundo sin depender de nadie.',
    reaction: 'Perfecto. Tu plan va a priorizar inglés funcional y conversacional real, no gramática de examen.',
    short: 'viajar y conectar sin depender de nadie',
  },
  {
    value: 'graduate_or_certify',
    icon: '🎓',
    label: 'Obtener mi título, certificación académica o beca.',
    reaction: 'Entendido. Vamos a estructurar tu ruta pensando en superar evaluaciones y métricas medibles.',
    short: 'obtener tu título o certificación',
  },
  {
    value: 'stop_staying_silent',
    icon: '🗣️',
    label: 'Sentirme seguro de mí mismo y dejar de quedarme callado.',
    reaction: 'Ese es el objetivo más valioso. Vamos a apostarle 100% a destrabar tu fluidez y confianza.',
    short: 'sentirte seguro y dejar de quedarte callado',
  },
  {
    value: CUSTOM_OPTION,
    icon: '✍️',
    label: 'Otro objetivo distinto...',
    reaction: 'Interesante objetivo. Vamos a registrar esto para que Spacy adapte tu plan de entrenamiento exactamente a esa meta.',
    // Mecánica UI del documento: revela un input de texto libre, máx. 100 chars.
    isCustom: true,
    placeholder: 'Escribe tu meta aquí...',
    maxLength: 100,
    short: 'tu objetivo',
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 2 — El Detonante y la Intensidad (§2) [ACTUALIZADO v2.1]
   ───────────────────────────────────────────────────────────────────────────── */

export const SCREEN2_HEADLINE = {
  linea: 'Algo pasó esta semana — o este mes — que te trajo hasta aquí. ',
  bloque: '¿Qué te motivó a dar el primer paso hoy?',
};

export const TRIGGERS = [
  {
    value: 'felt_at_disadvantage',
    icon: '😳',
    label: 'Una situación me hizo sentir en desventaja (entrevista, llamada, viaje).',
    reaction: 'El idioma es un músculo. Vamos a entrenarlo para que no vuelvas a sentir esa desventaja nunca más.',
  },
  {
    value: 'clear_goal_with_date',
    icon: '🎯',
    label: 'Tengo una meta clara y una fecha en mente.',
    reaction: 'Perfecto. Entrenar con una fecha límite es la mejor forma de ver resultados reales y medibles.',
  },
  {
    value: 'tried_magic_courses',
    icon: '🔁',
    label: 'Ya probé "cursos mágicos" y esta vez quiero entrenar en serio.',
    reaction: 'Se acabó el humo. Aquí no hay magia, hay entrenamiento constante adaptado a tu nivel.',
  },
  {
    value: 'self_decided_its_time',
    icon: '🌱',
    label: 'Nadie me empujó, simplemente decidí que es momento de avanzar.',
    reaction: 'Esa es la mejor motivación. La disciplina siempre supera a cualquier curso milagroso.',
  },
  {
    value: CUSTOM_OPTION,
    icon: '✍️',
    label: 'Otro motivo distinto...',
    reaction: 'Entendido. Conocer tu detonante real nos ayuda a mantener tu motivación alta durante el entrenamiento.',
    isCustom: true,
    placeholder: 'Cuéntanos tu motivo...',
    maxLength: 100,
  },
];

// [v2.1] Sustituye por completo a la pregunta de deadline de v2: NSL ya no
// promete fechas mágicas de fluidez, el usuario elige cuánto peso levanta.
export const SCREEN2_INTENSITY_HEADLINE = {
  linea: 'En un gimnasio, tú eliges el peso. ',
  bloque: '¿Qué nivel de intensidad estás dispuesto a meterle a este entrenamiento?',
};

export const TRAINING_INTENSITIES = [
  {
    value: 'bootcamp_intensivo',
    icon: '🚀',
    label: 'Bootcamp Intensivo.',
    hint: 'Quiero darle duro todos los días, mi prioridad es destrabarme ya',
    reaction: 'Modo bestia activado. Spacy será exigente y te sacará de tu zona de confort rápido.',
    short: 'bootcamp intensivo',
  },
  {
    value: 'constancia_maraton',
    icon: '🏃',
    label: 'Constancia de Maratón.',
    hint: 'Paso a paso. Busco crear un hábito sin quemarme',
    reaction: 'Excelente elección. La consistencia siempre le gana a la intensidad a largo plazo. Crearemos un hábito invencible.',
    short: 'constancia de maratón',
  },
  {
    value: 'reconstruccion_bases',
    icon: '🛠️',
    label: 'Reconstrucción de Bases.',
    hint: 'No tengo prisa, quiero ir despacio y corregir mis errores desde la raíz',
    reaction: 'Entendido. Nos enfocaremos en la técnica. Vamos a demoler esos malos hábitos y construir cimientos de acero.',
    short: 'reconstrucción de bases',
  },
  {
    value: 'sparring_libre',
    icon: '🥊',
    label: 'Sparring Libre.',
    hint: 'Solo necesito un compañero para practicar, soltar la lengua y no oxidarme',
    reaction: 'Cero presiones. Spacy será tu compañero de práctica diario para que el músculo no se oxide.',
    short: 'sparring libre',
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 3 — Contexto de Uso (§3) — sin cambios en v2.1/v2.2
   ───────────────────────────────────────────────────────────────────────────── */

export const SCREEN3_HEADLINE = {
  linea: '¿Dónde vas a usar tu inglés ',
  bloque: 'en el mundo real?',
};

export const CONTEXTS = [
  {
    value: 'business_tech',
    icon: '💼',
    label: 'Trabajo, negocios o tecnología',
    reaction: 'Eso significa que el vocabulario genérico de las apps típicas no te va a servir de mucho — tú necesitas otra cosa, y de eso nos encargamos.',
    short: 'trabajo, negocios y tecnología',
    processing: 'negocios y tecnología',   // §7.1: "Alineando vocabulario de [context_type]..."
  },
  {
    value: 'academic',
    icon: '🎓',
    label: 'Estudios o certificación académica',
    reaction: 'Vamos a priorizar estructura y precisión gramatical, que es justo lo que se evalúa en contextos académicos.',
    short: 'estudios y certificación académica',
    processing: 'contexto académico',
  },
  {
    value: 'travel_social',
    icon: '✈️',
    label: 'Viajes y vida social',
    reaction: 'Entonces lo que más te va a servir es fluidez natural, no reglas de examen — así vamos a enfocarlo.',
    short: 'viajes y vida social',
    processing: 'viajes y vida social',
  },
  {
    value: 'a_bit_of_everything',
    icon: '🌐',
    label: 'Un poco de todo lo anterior',
    reaction: 'Perfecto, vamos a mantener tu plan flexible en vocabulario en vez de encasillarlo en un solo contexto.',
    short: 'un poco de todo',
    processing: 'uso mixto',
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 4 — Autopercepción + Intentos Previos (§4) — sin cambios
   ───────────────────────────────────────────────────────────────────────────── */

export const SCREEN4_HEADLINE = {
  linea: 'Sé honesto contigo, no con nosotros: ',
  bloque: '¿cuál dirías que es tu nivel hoy?',
};

export const PERCEIVED_LEVELS = [
  {
    value: 'zero_absolute',
    icon: '🔴',
    label: 'Cero absoluto, casi no entiendo nada',
    skipsCalibration: true,   // §4 Bifurcación: salta directo a Pantalla 6
    display: 'cero absoluto',
  },
  {
    value: 'basic',
    icon: '🟡',
    label: 'Básico — entiendo cosas simples (A1-A2)',
    skipsCalibration: false,
    display: 'Básico',
  },
  {
    value: 'intermediate',
    icon: '🟢',
    label: 'Intermedio — me defiendo pero me trabo (B1-B2)',
    skipsCalibration: false,
    display: 'Intermedio',
  },
  {
    value: 'advanced',
    icon: '🔵',
    label: 'Avanzado — hablo con soltura casi siempre (C1)',
    skipsCalibration: false,
    display: 'Avanzado',
  },
];

export const SCREEN4_ATTEMPTS_HEADLINE = {
  linea: '¿Ya intentaste aprender inglés ',
  bloque: 'en serio antes?',
};

export const PAST_ATTEMPTS = [
  {
    value: 'apps_abandoned',
    icon: '📱',
    label: 'Sí, con apps — lo dejé sin avanzar mucho',
    reaction: 'Tiene sentido. Las apps genéricas enseñan lo mismo a todos, sin importar para qué lo necesitas tú.',
    short: 'apps genéricas',   // §7.1 y §7.2: "[past_attempts]"
  },
  {
    value: 'traditional_classes',
    icon: '👨‍🏫',
    label: 'Sí, clases tradicionales — no vi el progreso que esperaba',
    reaction: 'Es muy común — las clases grupales avanzan al ritmo del grupo, no al tuyo.',
    short: 'clases tradicionales',
  },
  {
    value: 'self_taught',
    icon: '🧠',
    label: 'Sí, por mi cuenta (series, música, redes) — pero sin estructura',
    reaction: 'Eso normalmente da buen oído pero bases débiles. Lo vamos a confirmar en el siguiente paso.',
    short: 'aprender por tu cuenta',
  },
  {
    value: 'first_time',
    icon: '🚫',
    label: 'No, esta es mi primera vez tomándolo en serio',
    reaction: 'Entonces no traes vicios que corregir — eso también es una ventaja.',
    short: 'intentos anteriores',
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 5 — Calibración (§0, §5.1, §5.2)
   ───────────────────────────────────────────────────────────────────────────── */

export const TRANSPARENCY_COPY =
  'Ojo: esto no es un examen oficial ni te va a dar un certificado Cambridge — para eso se necesitan horas, no segundos. Lo que sí hace es darnos una fotografía rápida y honesta de por dónde vas realmente, para no perder tiempo poniéndote contenido que ya sabes o que te queda grande. Tu nivel exacto lo vamos a seguir afinando sesión a sesión, esto es solo el punto de partida.';

export const CALIBRATION_INTRO = {
  linea: 'Ok, hablemos claro. ',
  bloque: 'Esto no es un examen ',
  bloqueAmarillo: 'Cambridge',
  linea2: 'Es una calibración de 60 segundos para no perder tiempo poniéndote cosas que ya sabes o que te quedan grandes. Vamos.',
};

// §5.2 Cierre de honestidad — SIEMPRE se muestra.
// [v2.2] Ya no dice "estas 6 preguntas": el banco adaptativo muestra un número
// variable de slides, así que la cifra fija era incorrecta.
export const CALIBRATION_HONESTY_CLOSE =
  'Recuerda: esto es tu punto de partida, no una etiqueta fija. Cada sesión real que hagas nos da información más precisa que estas preguntas — y tu plan se va a ir ajustando solo.';

// Micro-feedback por slide — nunca "correcto/incorrecto" en seco.
export const FEEDBACK_CORRECT = ['¡Justo eso!', 'Ahí está.'];
export const FEEDBACK_INCORRECT = ['Casi — mira esto:', 'No exactamente, pero vas bien encaminado:'];

/**
 * El contraste percepción vs. realidad — §5.2.
 * Devuelve el copy exacto según el cruce de variables definido en el documento.
 */
export function getContrastCopy(perceivedLevel, technicalScore) {
  if (perceivedLevel === 'intermediate' && technicalScore < 4) {
    return 'Interesante. Tu instinto conversacional va más adelantado que tu gramática formal — es un patrón súper común en gente que aprendió por su cuenta. Vamos a reforzar las bases sin que se sienta como volver a empezar de cero.';
  }
  if (perceivedLevel === 'basic' && technicalScore > 7) {
    return 'Vas mejor de lo que pensabas. Muchas veces el problema no es el nivel, es la confianza para usarlo — a eso también le vamos a apostar.';
  }
  // "Si coinciden" — también el caso por defecto: el documento solo define copy
  // para estos tres cruces, y el neutral es el único que no afirma nada falso.
  return 'Tu autopercepción está bastante alineada con tu nivel real — eso nos ahorra tiempo y nos deja enfocarnos 100% en avanzar, no en corregir el diagnóstico.';
}

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 5.5 — Captura de Contacto (§5.bis)
   ───────────────────────────────────────────────────────────────────────────── */

export const CONTACT_CAPTURE = {
  headline:
    'Antes de seguir — ¿a dónde te mando este diagnóstico? Así no lo pierdes si cierras esto sin querer, y puedo avisarte cuando tu plan esté listo.',
  emailLabel: 'Email',
  emailIcon: '📧',
  phoneLabel: 'WhatsApp (opcional)',
  phoneIcon: '📱',
  // Se nombra México a propósito: la lada va fija en +52 y así nadie de otro
  // país escribe un número que no vamos a poder usar.
  phoneMicrocopy: 'Diez cifras, número de México. Te confirmamos antes de escribirte.',
  trustMicrocopy:
    'Nada de spam. Solo tu resultado, y si nos das tu WhatsApp, un recordatorio si dejas esto a medias — tú decides.',
  primaryButton: 'Guardar mi resultado →',
  skipLink: 'Prefiero verlo sin guardarlo',   // link de texto, NUNCA un botón
};

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 6 — Estilo de Entrenamiento / Feedback (§6) [REDISEÑADA v2.2]

   Sustituye por completo a la Pantalla 6 de v2 (tiempo diario + estilo de
   aprendizaje). El valor elegido alimenta directamente el System Prompt de
   Spacy mediante la variable `feedback_style`.
   ───────────────────────────────────────────────────────────────────────────── */

export const SCREEN6_HEADLINE = {
  linea: 'Todo buen entrenador sabe cómo exigirle a su atleta. Cuando hables con Spacy y cometas un error, ',
  bloque: '¿cómo quieres que te corrija?',
};

export const FEEDBACK_STYLES = [
  {
    value: 'strict_immediate',
    icon: '🛑',
    label: 'El Sargento (Corrección Inmediata).',
    hint: 'Detenme en el segundo que me equivoque, quiero gramática perfecta',
    reaction: 'Entendido. Spacy será implacable. No dejaremos pasar ni un solo error de pronunciación o estructura.',
    short: 'El Sargento',
  },
  {
    value: 'delayed_summary',
    icon: '🥊',
    label: 'El Sparring (Fluidez primero, corrección al final).',
    hint: 'Déjame terminar de hablar para no perder la confianza, y dame un resumen al final',
    reaction: 'Excelente. Priorizaremos que pierdas el miedo a hablar. Spacy tomará nota y te dará feedback al cerrar la ronda.',
    short: 'El Sparring',
  },
  {
    value: 'critical_only',
    icon: '🧭',
    label: 'El Guía (Solo errores críticos).',
    hint: 'Solo interrúmpeme si digo algo que arruine por completo lo que intento comunicar',
    reaction: 'Trato hecho. Entrenaremos para la vida real: mientras el mensaje se entienda, la conversación sigue.',
    short: 'El Guía',
  },
];

/* ─────────────────────────────────────────────────────────────────────────────
   PANTALLA 7 — Revelación + Cierre de Venta (§7.1, §7.2)
   ───────────────────────────────────────────────────────────────────────────── */

/**
 * Mensajes de la pantalla de procesamiento — §7.1.
 * [v2.2] Son 4: el mensaje de deadline fue sustituido por el de estilo de
 * feedback, y se eliminó el de "afinando los últimos detalles".
 */
export function getProcessingMessages(lookups) {
  const { contextLabel, feedbackLabel, attemptsLabel } = lookups;
  return [
    'Cruzando tu prueba de calibración con tu nivel percibido...',
    `Alineando vocabulario de ${contextLabel}...`,
    `Ajustando tu estilo de feedback a ${feedbackLabel}...`,
    `Descartando lo que no te funcionó en ${attemptsLabel}...`,
  ];
}

/**
 * Bloques a) b) c) d) e) del resultado — §7.2, en orden y con variables.
 * Copy literal del documento; solo se sustituyen los [placeholders].
 */
export function getRevelationBlocks({
  realLevel,       // [X] — nivel real estimado por la calibración
  perceivedLabel,  // [Y] — nivel percibido
  attemptsLabel,   // [past_attempts]
  contextLabel,    // [context_type]
  feedbackLabel,   // [feedback_style]
  aspirationLabel, // [aspiration]
}) {
  return {
    // a) El espejo
    mirror: `Tu inglés real hoy está cerca de nivel ${realLevel}, aunque sentías que estabas en ${perceivedLabel}. No es un error tuyo — es lo que pasa cuando aprendes solo. Y recuerda: esta es tu foto inicial, no tu límite.`,

    // b) La causa raíz
    //    ⚠️ El documento v2.2 dejó aquí la variable [learning_style], que el
    //    propio v2.2 eliminó del payload al rediseñar la Pantalla 6. Se enlaza
    //    a `feedback_style`, que es la variable que ahora describe la forma en
    //    que el usuario quiere ser corregido — es decir, cómo aprende.
    rootCause: `Probaste ${attemptsLabel} y no avanzaste como querías — no por falta de disciplina, sino porque ese método no se ajustaba a tu contexto de ${contextLabel} ni a tu forma real de aprender (${feedbackLabel}). Eso es exactamente lo que este plan sí resuelve.`,

    // c) El plan
    plan: `Tu ruta hacia ${aspirationLabel} está lista: enfocada en ${contextLabel}, calibrada a tu punto de partida real, configurada con tu estilo de feedback ${feedbackLabel}, con el ritmo de entrenamiento que elegiste.`,

    // d) El argumento económico
    economic: 'Cada mes que sigues intentándolo por tu cuenta con apps gratuitas o métodos genéricos no es gratis — tiene un costo invisible. Aprender sin un punto de partida claro es como entrenar sin saber tu peso inicial: puedes esforzarte meses y seguir estancado en lo mismo. Este plan no es un gasto adicional — es lo que evita que dentro de seis meses sigas exactamente donde estás hoy.',

    // e) CTA final
    cta: 'Tu plan de entrenamiento está listo. Crea tu cuenta y empieza tu primera sesión hoy.',
  };
}

export const REVELATION_BUTTON = 'Quiero mi plan →';

/* ─────────────────────────────────────────────────────────────────────────────
   §7.3 — Dos estados de retorno
   ───────────────────────────────────────────────────────────────────────────── */

export const RETURN_BUTTON_IN_PROGRESS = 'Continuar mi diagnóstico →';

// [v2.2] Ya no interpola [aspiration]: el copy aprobado es fijo.
export const RETURN_BUTTON_COMPLETED = 'Tu plan de entrenamiento sigue listo →';
